import React, { useState, useEffect } from "react";
import { QUESTION_TYPES } from "../_constant";
import Select from "react-select";
import { FaPlus, FaTrash, FaCheck, FaTimes, FaEdit } from "react-icons/fa";
import { DependentQuestionForm } from "./DependentQuestionForm";

const QuestionForm = ({ onSave, onCancel, question = {} }) => {
  const [text, setText] = useState(question.text || "");
  const [type, setType] = useState(question.type || "text");
  const [isRequired, setIsRequired] = useState(question.is_required || false);
  const [options, setOptions] = useState(
    question.options?.map((opt) => ({
      ...opt,
      dependent_questions: opt.dependent_questions || [],
    })) || []
  );
  const [dependentQuestions, setDependentQuestions] = useState({
    yes: question.dependent_questions?.yes || [],
    no: question.dependent_questions?.no || [],
  });
  const [newOption, setNewOption] = useState("");
  const [error, setError] = useState("");
  const [editingDependent, setEditingDependent] = useState(null);
  const [isEditingDependent, setIsEditingDependent] = useState(false);

  useEffect(() => {
    if (
      !["multiple_choice", "checkbox_group", "dropdown", "yes_no"].includes(
        type
      )
    ) {
      setOptions([]);
    }
  }, [type]);

  const handleAddOption = () => {
    if (!newOption.trim()) {
      setError("Option cannot be empty.");
      return;
    }
    setOptions((prev) => [
      ...prev,
      { label: newOption.trim(), dependent_questions: [] },
    ]);
    setNewOption("");
    setError("");
  };

  const handleRemoveOption = (index) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddDependentQuestion = (target, responseType = null) => {
    setEditingDependent({ target, responseType });
    setIsEditingDependent(false);
  };

  const handleEditDependentQuestion = (
    target,
    depIndex,
    responseType = null
  ) => {
    if (responseType) {
      setEditingDependent({
        target,
        responseType,
        question: dependentQuestions[responseType][depIndex],
        depIndex,
      });
    } else {
      setEditingDependent({
        target,
        question: options[target].dependent_questions[depIndex],
        depIndex,
      });
    }
    setIsEditingDependent(true);
  };

  const handleSaveDependentQuestion = (
    target,
    newQuestion,
    responseType = null
  ) => {
    if (responseType) {
      setDependentQuestions((prev) => {
        const updatedQuestions = [...prev[responseType]];
        if (isEditingDependent) {
          updatedQuestions[editingDependent.depIndex] = newQuestion;
        } else {
          updatedQuestions.push(newQuestion);
        }
        return { ...prev, [responseType]: updatedQuestions };
      });
    } else {
      setOptions((prev) => {
        const updatedOptions = [...prev];
        if (isEditingDependent) {
          updatedOptions[target].dependent_questions[
            editingDependent.depIndex
          ] = newQuestion;
        } else {
          updatedOptions[target].dependent_questions.push(newQuestion);
        }
        return updatedOptions;
      });
    }
    setEditingDependent(null);
    setIsEditingDependent(false);
  };

  const handleRemoveDependentQuestion = (
    target,
    depIndex,
    responseType = null
  ) => {
    if (responseType) {
      setDependentQuestions((prev) => ({
        ...prev,
        [responseType]: prev[responseType].filter((_, i) => i !== depIndex),
      }));
    } else {
      setOptions((prev) => {
        const updatedOptions = [...prev];
        updatedOptions[target].dependent_questions.splice(depIndex, 1);
        return updatedOptions;
      });
    }
  };

  const handleSubmit = () => {
    if (!text.trim()) {
      setError("Question text is required.");
      return;
    }

    const newQuestion = {
      ...question,
      text,
      type,
      is_required: isRequired,
      options: ["multiple_choice", "checkbox_group", "dropdown"].includes(type)
        ? options
        : undefined,
      dependent_questions: type === "yes_no" ? dependentQuestions : undefined,
    };

    onSave(newQuestion);
  };

  const questionTypeOptions = QUESTION_TYPES.map((opt) => ({
    value: opt.value,
    label: opt.label,
    icon: opt.icon,
  }));

  return (
    <div className="p-6 border rounded shadow bg-white">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        {question.question_id ? <FaCheck /> : <FaPlus />}{" "}
        {question.question_id ? "Edit Question" : "Add Question"}
      </h2>
      {error && <div className="text-red-500 mb-4">{error}</div>}
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-1">
          Question Text:
        </label>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Enter the question text"
        />
      </div>
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-1">Type:</label>
        <Select
          value={questionTypeOptions.find((opt) => opt.value === type)}
          onChange={(selected) => setType(selected.value)}
          options={questionTypeOptions}
          className="basic-single"
          classNamePrefix="select"
        />
      </div>
      <div className="mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          checked={isRequired}
          onChange={(e) => setIsRequired(e.target.checked)}
          className="mr-2"
        />
        <span className="text-gray-600">Mark this question as required</span>
      </div>
      {["multiple_choice", "checkbox_group", "dropdown"].includes(type) && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">
            Options and Dependencies:
          </label>
          {options.map((option, index) => (
            <div key={index} className="mb-4 bg-gray-100 p-4 rounded shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-800 font-semibold">
                  {option.label}
                </span>
                <button
                  onClick={() => handleRemoveOption(index)}
                  className="text-red-500 hover:text-red-600"
                >
                  <FaTrash />
                </button>
              </div>
              <div className="mt-2">
                <button
                  onClick={() => handleAddDependentQuestion(index)}
                  className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 flex items-center gap-2"
                >
                  <FaPlus /> Add Dependent Question
                </button>
                {option.dependent_questions?.length > 0 && (
                  <div className="mt-4">
                    {option.dependent_questions.map((dep, depIndex) => (
                      <div
                        key={depIndex}
                        className="flex items-center justify-between bg-gray-50 p-2 rounded shadow-sm mb-2"
                      >
                        <span>
                          Dependent Question {depIndex + 1}: {dep.text || ""}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              handleEditDependentQuestion(index, depIndex)
                            }
                            className="text-blue-500 hover:text-blue-600"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() =>
                              handleRemoveDependentQuestion(index, depIndex)
                            }
                            className="text-red-500 hover:text-red-600"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              className="border p-2 flex-grow rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter an option"
            />
            <button
              onClick={handleAddOption}
              className="bg-green-500 text-white px-4 py-2 rounded shadow hover:bg-green-600 flex items-center gap-2"
            >
              <FaPlus /> Add Option
            </button>
          </div>
        </div>
      )}
      {type === "yes_no" && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">
            Yes/No Dependencies:
          </label>
          <div className="mb-4">
            <h4 className="font-semibold text-gray-700">Yes:</h4>
            {dependentQuestions.yes.map((question, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-100 p-2 rounded shadow-sm mb-2"
              >
                <span>
                  Dependent Question {index + 1}: {question.text || ""}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      handleEditDependentQuestion(null, index, "yes")
                    }
                    className="text-blue-500 hover:text-blue-600"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() =>
                      handleRemoveDependentQuestion(null, index, "yes")
                    }
                    className="text-red-500 hover:text-red-600"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={() => handleAddDependentQuestion(null, "yes")}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 flex items-center gap-2"
            >
              <FaPlus /> Add Question for Yes
            </button>
          </div>
          <div>
            <h4 className="font-semibold text-gray-700">No:</h4>
            {dependentQuestions.no.map((question, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-100 p-2 rounded shadow-sm mb-2"
              >
                <span>
                  Dependent Question {index + 1}: {question.text || ""}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      handleEditDependentQuestion(null, index, "no")
                    }
                    className="text-blue-500 hover:text-blue-600"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() =>
                      handleRemoveDependentQuestion(null, index, "no")
                    }
                    className="text-red-500 hover:text-red-600"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={() => handleAddDependentQuestion(null, "no")}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 flex items-center gap-2"
            >
              <FaPlus /> Add Question for No
            </button>
          </div>
        </div>
      )}
      {editingDependent && (
        <DependentQuestionForm
          onSave={(newQuestion) =>
            handleSaveDependentQuestion(
              editingDependent.target,
              newQuestion,
              editingDependent.responseType
            )
          }
          onCancel={() => setEditingDependent(null)}
          question={editingDependent.question || {}}
          isEditing={isEditingDependent}
          parentQuestionName={text}
        />
      )}
      <div className="flex justify-end gap-2 mt-6">
        <button
          onClick={onCancel}
          className="bg-gray-500 text-white px-4 py-2 rounded shadow hover:bg-gray-600 flex items-center gap-2"
        >
          <FaTimes /> Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 flex items-center gap-2"
        >
          <FaCheck /> Save
        </button>
      </div>
    </div>
  );
};

export default QuestionForm;
